import React, {useState, useContext, useLayoutEffect} from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { API, Auth } from 'aws-amplify';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import AnimatedRoute from '../components/AnimatedRoute';
import Button from '../components/Button';
import OshiSelect from '../components/OshiSelect';
import OshiSelectOption from '../components/OshiSelectOption';
import OshiInput from '../components/OshiInput';
import SignedInAs from '../components/SignedInAs';
import { CardscanContext } from '../utils/context';
import { setIsFetching } from '../actions';
import { segmentTypes } from '../constants';
import { createTrackEvent, TrackNonPolicyHolder } from '../actions/segment';
import { ROUTES } from '../constants/routes';

const listValues = [ 'Parent', 'Child', 'Spouse', 'Other'];

const NonPolicyHolderRoute = ({ setIsFetching, TrackNonPolicyHolder }) => {
  const [animationDirection, setAnimationDirection] = useState('mount');
  const [relationshipValue, setRelationshipValue] = useState('');
  const [showOtherField, setShowOtherField] = useState(false);
  const [insuranceInformation, ] = useContext(CardscanContext);
  const history = useHistory();

  useLayoutEffect(() => {
    TrackNonPolicyHolder();
    // eslint-disable-next-line
  }, []);

  const dobValidate = ({ policyHolderBirthday }) => {
    const errors = {};
    const splitBirthday = policyHolderBirthday.split(' / ').join('/').split('/');
    const month = Number(splitBirthday[0]);
    const day = Number(splitBirthday[1]);

    if (policyHolderBirthday?.length < 14) {
      errors.length = true;
      return errors;
    }

    if (month < 1 || month > 12) {
      errors.month = true;
      return errors;
    }

    if (day < 1 || day > 31) {
      errors.day = true;
      return errors;
    }

    return errors;
  }

  const nameValidate = ({ policyHolderName }) => {
    const errors = {};
    if (policyHolderName?.length === 0) {
      errors.policyHolderName = true;
    }

    return errors;
  }

  const validate = ({ policyHolderName, policyHolderBirthday}) => ({
    ...nameValidate({policyHolderName}),
    ...dobValidate({policyHolderBirthday}),
  });



  const formik = useFormik({
    initialValues: {
      policyHolderName: '',
      policyHolderBirthday: '',
      policyHolderRelationship: '',
      policyHolderRelationshipOther: '',
    },
    validate,
    onSubmit: handleSubmit,
  });

  const isValid = () => {
    if (showOtherField && formik.values.policyHolderRelationshipOther === '' ) return false;
    return formik.dirty && Object.keys(formik.errors).length === 0 && relationshipValue !== '';
  }
  
  async function handleSubmit() {
    setIsFetching(true);
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const userId = cognitoUser.attributes.sub;

    const policy_holder_dob = (new Date(formik.values.policyHolderBirthday)).getTime();

    await API.post('oshiAPI', '/documents/insurance', {
      body: {
        ...insuranceInformation,
        is_policy_holder: false,
        relationship_to_policy_holder: showOtherField ? formik.values.policyHolderRelationshipOther : relationshipValue,
        policy_holder_name: formik.values.policyHolderName,
        policy_holder_dob,
        patient_cognito_id: userId }
    }).then(() => {
      setIsFetching(false);
      history.push(ROUTES.FIRST_APPT_OVERVIEW);
    }).catch(error => {
      console.error(error);
      createTrackEvent(segmentTypes.SERVER_ERROR, `Server error`);
      setIsFetching(false);
      history.push('network-error', { redirectLink: window.location.href });
    });
  }

  return (
    <AnimatedRoute
      title='Policy holder information'
      preHeader='Insurance information'
      showProgressBar={false}
      animationDirection={animationDirection}
      setAnimationDirection={setAnimationDirection}
    >
      <Form>
        <OshiInput
          id='policyHolderName'
          autofocus
          type='text'
          label='Policy holder full name'
          maskPlaceholder=''
          segmentLabel='Policy Holder - Full Name'
          value={formik.values.policyHolderName}
          onChange={formik.handleChange}
        />
        <OshiInput
          id='policyHolderBirthday'
          autoFocus
          type='tel'
          pattern='\d*'
          label='Policy holder date of birth'
          isValid={() => isValid()}
          formik={formik}
          maskPlaceholder=''
          onChange={formik.handleChange}
          value={formik.values.policyHolderBirthday}
          segmentLabel='Policy Holder - Birthday'
          mask='99 / 99 / 9999'
          secondaryLabel='mm / dd / yyyy'
        />

        <DropdownContainer>
        <OshiSelect
          name='PolicyHolderRelationship'
          onChange={relationship => {
            setRelationshipValue(relationship);
            if (relationship === 'Other') {
              setShowOtherField(true);
            } else {
              setShowOtherField(false);
            }
          }}
          label='Your relationship to policy holder'
        >
          { listValues.map((value) => (
            <OshiSelectOption
              key={value}
              value={value}
              label={value}
            />
          ))}
        </OshiSelect>
        { showOtherField && 
          <OshiInput
            id='policyHolderRelationshipOther'
            autofocus
            type='text'
            label='Other Relationship'
            segmentLabel='Policy Holder - Relationship'
            value={formik.values.policyHolderRelationshipOther}
            onChange={formik.handleChange}
          />
        }
        </DropdownContainer>
      </Form>
      <Button styles={{ width: '100%' }} disabled={ !isValid() } onClick={handleSubmit}>Submit</Button>
      <SignedInAs
          styles={{ flex: 1, alignItems: 'flex-end', justifyContent: 'center' }}
      />
    </AnimatedRoute>
  );
}

const DropdownContainer = styled.div`
  margin: 24px 0;
  width: 100%;
`;

const Form = styled.form`
  width: 100%;
`;

export default connect(null, {setIsFetching, TrackNonPolicyHolder})(NonPolicyHolderRoute);